/**
 * Shared helpers for per-language client-view texts.
 *
 * Client-view texts live in three layers (per user spec):
 *   1. template.texts            — admin defaults, per-language
 *   2. board.custom_texts        — snapshot of template texts at board creation, per-language
 *   3. branding_overrides.texts  — per-user override, per-language
 *
 * Every layer uses the same shape:
 *   { en: { welcome_title: "...", ... }, pl: {...}, de: {...} }
 *
 * Legacy data may still be stored as a flat object (no en/pl/de keys).
 * `normalizeByLang` tolerates both shapes.
 */

export const TEXT_LANGS = ['en', 'pl', 'de'];

export const LANGUAGE_LABELS = {
  en: 'English',
  pl: 'Polski',
  de: 'Deutsch',
};

export const DEFAULT_TEXT_FIELDS = {
  welcome_title: '',
  welcome_subtitle: '',
  welcome_description: '',
  instruction_text: '',
  instruction_react: '',
  inspiration_title: '',
  inspiration_description: '',
  notes_title: '',
  notes_description: '',
  documents_title: '',
  documents_description: '',
};

/** Return true if obj is the legacy flat shape (no en/pl/de top-level keys). */
const isLegacyFlat = (obj) =>
  obj && typeof obj === 'object' &&
  !('en' in obj) && !('pl' in obj) && !('de' in obj) &&
  Object.keys(obj).length > 0;

/** Coerce any supported texts shape into {en:{}, pl:{}, de:{}, ...sharedSiblings}.
 *  Non-language top-level keys (e.g. `custom_footer_text`) are preserved as
 *  shared/language-agnostic fields so downstream code can read them flat. */
export const normalizeByLang = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return { en: {}, pl: {}, de: {} };
  }
  if (isLegacyFlat(raw)) {
    return { en: { ...raw }, pl: {}, de: {} };
  }
  const out = {
    en: raw.en && typeof raw.en === 'object' ? { ...raw.en } : {},
    pl: raw.pl && typeof raw.pl === 'object' ? { ...raw.pl } : {},
    de: raw.de && typeof raw.de === 'object' ? { ...raw.de } : {},
  };
  for (const [k, v] of Object.entries(raw)) {
    if (!TEXT_LANGS.includes(k)) {
      out[k] = v;
    }
  }
  return out;
};

/** True if the object has any *resolved* text value (including the
 *  intentional empty-string clear). A key whose value is `undefined` or
 *  `null` counts as "untouched"; everything else — including `""` —
 *  counts as a user override the resolver must honor. */
export const hasAnyTexts = (raw) => {
  const n = normalizeByLang(raw);
  return TEXT_LANGS.some(lang =>
    Object.values(n[lang] || {}).some(v => v !== undefined && v !== null)
  );
};

/** True if the resolved value should stop the fallback chain. `undefined`
 *  and `null` are "unresolved"; any string (including `""` — an
 *  intentional clear) stops the chain. */
const isResolved = (v) => v !== undefined && v !== null;

/**
 * Resolve a single text field per the render priority spec:
 *   If branding override has any text (incl. intentional empty clears):
 *     1. branding[lang][key]
 *     2. template[lang][key]
 *     3. template.en[key]
 *   Otherwise:
 *     1. template[lang][key]
 *     2. template.en[key]
 *
 * A resolved value of `""` (intentional empty) STOPS the chain and is
 * returned as-is — the client view renders nothing, matching the user's
 * cleared-field intent. `fallback` is used only if every layer is
 * unresolved (i.e. `undefined`/`null` everywhere).
 */
export const resolveText = ({ branding, template, lang, key, fallback = '' }) => {
  const b = normalizeByLang(branding);
  const t = normalizeByLang(template);
  const brandingHasAnything = hasAnyTexts(b);

  if (brandingHasAnything) {
    const fromBrandLang = b[lang]?.[key];
    if (isResolved(fromBrandLang)) return fromBrandLang;
    const fromTplLang = t[lang]?.[key];
    if (isResolved(fromTplLang)) return fromTplLang;
    const fromTplEn = t.en?.[key];
    if (isResolved(fromTplEn)) return fromTplEn;
  } else {
    const fromTplLang = t[lang]?.[key];
    if (isResolved(fromTplLang)) return fromTplLang;
    const fromTplEn = t.en?.[key];
    if (isResolved(fromTplEn)) return fromTplEn;
  }
  return fallback;
};
